import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, emailService, detectHarmContent, detectUnconstructiveContent, detectConnotationIssues, containsProfanity, notifyMentorOfHarmDetection, sendNotification, sendPushToUser, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase, loadRelationships, logHarmDetectionEvent, canCreateConflict } from '../shared.jsx';
import SayItYourWayPanel from './SayItYourWayPanel.jsx';

function CreateConflictView({ user, conflicts, userBilling, isGrandfathered, onBack, onCreated }) {
  const toast = useToast();
  const showCrisisModal = useCrisisAlert();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [sayItYourWayOpen, setSayItYourWayOpen] = useState(false);

  // Callback when Say It Your Way extracts structured conflict data
  const handleSayItYourWayExtracted = (extracted) => {
    setFormData(prev => ({
      ...prev,
      title: extracted.title || prev.title,
      who: extracted.who || prev.who,
      what: extracted.what || prev.what,
      when: extracted.when || prev.when,
      where: extracted.where || prev.where,
      how: extracted.how || prev.how,
      desiredOutcome: extracted.desiredOutcome || prev.desiredOutcome,
    }));
    toast('Fields pre-filled from your conversation!');
  };
  const [formData, setFormData] = useState({
    title: '',
    model: 'personal',
    urgency: 3,
    who: '',
    what: '',
    where: '',
    when: '',
    how: '',
    desiredOutcome: '',
    needByDate: '',
    responseHours: '9-5',
    customStartHour: null,
    customEndHour: null,
    responseWaitTime: 15,
    responseWindow: 15,
    maxCommunicationCycles: 5,
    aiParaphraseAssist: true,
    storyModeEnabled: true,
    menteeEmails: ['']
  });

  const [errors, setErrors] = useState({});
  const [deadlineAnalysis, setDeadlineAnalysis] = useState(null);

  // Calculate available working hours between now and a deadline
  const calculateAvailableMinutes = (deadline, responseHours) => {
    let startHour, endHour;
    if (responseHours === '24') {
      startHour = 0; endHour = 24;
    } else {
      const parts = responseHours.split('-').map(h => parseInt(h));
      startHour = parts[0] || 9; endHour = parts[1] || 17;
    }
    const hoursPerDay = startHour < endHour ? endHour - startHour : (24 - startHour + endHour);
    const now = new Date();
    const deadlineDate = new Date(deadline);
    if (deadlineDate <= now) return 0;
    const totalMs = deadlineDate - now;
    const totalDays = totalMs / (1000 * 60 * 60 * 24);
    // Rough: available minutes = total days × hours per day × 60
    return Math.floor(totalDays * hoursPerDay * 60);
  };

  // Calculate worst-case duration for given settings
  const calculateWorstCaseMinutes = (waitTime, responseWindow, maxCycles, numMentees) => {
    const n = Math.max(numMentees, 2);
    // Phase 1: Identify & Define — each mentee submits a paraphrase + each acknowledges others
    const identifyDefine = n * (waitTime + responseWindow) + n * (n - 1) * (waitTime + responseWindow);
    // Phase 2: Communication — each cycle: statement + paraphrase for each pair direction
    const communicate = maxCycles * 2 * (waitTime + responseWindow);
    // Phase 3: Explore Alternatives — each mentee submits up to 3
    const explore = n * 3 * (waitTime + responseWindow);
    // Phase 4: Evaluate — each mentee rates
    const evaluate = n * (waitTime + responseWindow);
    // Phase 5: Agree — each mentee acknowledges
    const agree = n * (waitTime + responseWindow);
    // Phase 6: Follow-up — each mentee rates
    const followUp = n * (waitTime + responseWindow);
    return identifyDefine + communicate + explore + evaluate + agree + followUp;
  };

  // Adjust timings to fit deadline, returns adjusted settings + explanation
  const adjustTimingsForDeadline = (deadline, currentSettings) => {
    const numMentees = Math.max(currentSettings.menteeEmails?.filter(e => e.trim() && e.includes('@')).length || 1, 1) + 1; // +1 for creator
    const available = calculateAvailableMinutes(deadline, currentSettings.responseHours);
    
    if (available <= 0) {
      return { fits: false, reason: 'The deadline has already passed.', adjustments: [] };
    }

    let waitTime = currentSettings.responseWaitTime;
    let responseWindow = currentSettings.responseWindow;
    let maxCycles = currentSettings.maxCommunicationCycles || 5;
    const adjustments = [];

    // Check if current settings fit
    let worstCase = calculateWorstCaseMinutes(waitTime, responseWindow, maxCycles, numMentees);
    if (worstCase <= available) {
      return { fits: true, worstCase, available, waitTime, responseWindow, maxCycles, adjustments };
    }

    // Step 1: Reduce wait time (cooldown) first
    const waitOptions = [15, 10, 5];
    for (const w of waitOptions) {
      if (w < waitTime) {
        waitTime = w;
        worstCase = calculateWorstCaseMinutes(waitTime, responseWindow, maxCycles, numMentees);
        if (worstCase <= available) {
          adjustments.push(`Cooldown reduced to ${waitTime} min`);
          return { fits: true, worstCase, available, waitTime, responseWindow, maxCycles, adjustments };
        }
      }
    }
    if (waitTime !== currentSettings.responseWaitTime) {
      adjustments.push(`Cooldown reduced to ${waitTime} min`);
    }

    // Step 2: Reduce max communication cycles
    const cycleOptions = [4, 3, 2];
    for (const c of cycleOptions) {
      if (c < maxCycles) {
        maxCycles = c;
        worstCase = calculateWorstCaseMinutes(waitTime, responseWindow, maxCycles, numMentees);
        if (worstCase <= available) {
          adjustments.push(`Max communication cycles reduced to ${maxCycles}`);
          return { fits: true, worstCase, available, waitTime, responseWindow, maxCycles, adjustments };
        }
      }
    }
    if (maxCycles !== (currentSettings.maxCommunicationCycles || 5)) {
      adjustments.push(`Max communication cycles reduced to ${maxCycles}`);
    }

    // Step 3: Reduce response window (last resort)
    const windowOptions = [15, 10, 5];
    for (const rw of windowOptions) {
      if (rw < responseWindow) {
        responseWindow = rw;
        worstCase = calculateWorstCaseMinutes(waitTime, responseWindow, maxCycles, numMentees);
        if (worstCase <= available) {
          adjustments.push(`Response window reduced to ${responseWindow} min`);
          return { fits: true, worstCase, available, waitTime, responseWindow, maxCycles, adjustments };
        }
      }
    }
    if (responseWindow !== currentSettings.responseWindow) {
      adjustments.push(`Response window reduced to ${responseWindow} min`);
    }

    // Still doesn't fit even at minimums
    return {
      fits: false,
      worstCase,
      available,
      waitTime,
      responseWindow,
      maxCycles,
      adjustments,
      reason: `Even with minimum settings (${waitTime}min cooldown, ${responseWindow}min window, ${maxCycles} cycles), the worst-case timeline (${formatMinutes(worstCase)}) exceeds the available time (${formatMinutes(available)}).`
    };
  };

  const formatMinutes = (mins) => {
    if (mins < 60) return `${Math.round(mins)} min`;
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    if (h < 24) return m > 0 ? `${h}h ${m}m` : `${h}h`;
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
  };

  // Recalculate deadline analysis whenever relevant fields change
  React.useEffect(() => {
    if (!formData.needByDate) {
      setDeadlineAnalysis(null);
      return;
    }
    const analysis = adjustTimingsForDeadline(formData.needByDate, formData);
    setDeadlineAnalysis(analysis);
  }, [formData.needByDate, formData.responseWaitTime, formData.responseWindow, formData.responseHours, formData.maxCommunicationCycles, formData.menteeEmails]);

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.who.trim()) newErrors.who = 'Who is required';
    if (!formData.what.trim()) newErrors.what = 'What is required';
    if (!formData.where.trim()) newErrors.where = 'Where is required';
    if (!formData.when.trim()) newErrors.when = 'When is required';
    if (!formData.how.trim()) newErrors.how = 'How is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.desiredOutcome.trim()) newErrors.desiredOutcome = 'Desired outcome is required';
    const validEmails = formData.menteeEmails.filter(e => e.trim() && e.includes('@'));
    if (validEmails.length < 1) newErrors.menteeEmails = 'At least one participant email is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [deadlineShake, setDeadlineShake] = useState(false);

  const handleNext = () => {
    // On step 1, warn if deadline doesn't fit
    if (step === 1 && deadlineAnalysis && !deadlineAnalysis.fits) {
      setDeadlineShake(true);
      setTimeout(() => setDeadlineShake(false), 600);
      toast('Your deadline cannot be met with current settings. Adjust the deadline or timing.');
      return;
    }
    if (step === 1 && deadlineAnalysis && deadlineAnalysis.adjustments.length > 0) {
      setDeadlineShake(true);
      setTimeout(() => setDeadlineShake(false), 600);
      toast('Apply the suggested adjustments or change your settings to fit the deadline.');
      return;
    }
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3 && validateStep3()) handleSubmit();
  };

  const handleSubmit = async () => {
    setSaving(true);

    try {
      // Plan enforcement: verify user can create another conflict (defense in depth)
      if (!isGrandfathered) {
        const activeCount = (conflicts || []).filter(c => c.status !== 'resolved' && c.createdBy === user.id).length;
        const check = canCreateConflict(userBilling, null, activeCount);
        if (!check.allowed) {
          toast(check.reason);
          setSaving(false);
          return;
        }
      }

      // Block profanity and unconstructive content in creation fields
      const fieldsToCheck = [
        { text: formData.title, label: 'conflict title' },
        { text: formData.what, label: '"What happened" field' },
        { text: formData.who, label: '"Who is involved" field' },
        { text: formData.where, label: '"Where did it happen" field' },
        { text: formData.when, label: '"When did it happen" field' },
        { text: formData.how, label: '"How did it happen" field' },
        { text: formData.desiredOutcome, label: '"Desired outcome" field' },
      ];
      for (const field of fieldsToCheck) {
        if (!field.text) continue;
        if (containsProfanity(field.text)) {
          toast(`Please remove profanity from the ${field.label}`);
          setSaving(false);
          return;
        }
        const toneCheck = detectUnconstructiveContent(field.text);
        if (toneCheck.isUnconstructive) {
          toast(`Please revise the ${field.label}: ${toneCheck.reason}`);
          setSaving(false);
          return;
        }
      }
      // Warn on connotation issues (Tier 2 — non-blocking, shows alternatives)
      for (const field of fieldsToCheck) {
        if (!field.text) continue;
        const connotation = detectConnotationIssues(field.text);
        if (connotation.hasIssues) {
          const alternatives = connotation.issues.map(i => `"${i.word}" → try "${i.suggestion}"`).join('; ');
          toast(`Loaded language in ${field.label}: ${alternatives}`);
        }
      }

      const conflict = {
        id: `conflict-${Date.now()}`,
        title: formData.title,
        model: formData.model,
        problemStatement: {
          who: formData.who,
          what: formData.what,
          where: formData.where,
          when: formData.when,
          how: formData.how
        },
        desiredOutcome: formData.desiredOutcome,
        actualOutcome: '',
        status: 'pending-acceptance',
        statusHistory: [{ status: 'pending-acceptance', timestamp: new Date().toISOString() }],
        tenantId: user.tenantId || 'tenant-public',
        createdBy: user.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        updatedBy: user.id,
        responseHours: formData.responseHours,
        responseWaitTime: formData.responseWaitTime,
        responseWindow: formData.responseWindow,
        urgency: formData.urgency,
        needByDate: formData.needByDate || null,
        maxCommunicationCycles: formData.maxCommunicationCycles || 5,
        aiParaphraseAssist: formData.aiParaphraseAssist,
        storyModeEnabled: formData.storyModeEnabled,
        termsAcceptance: {
          required: true,
          acceptedBy: [user.id], // Creator automatically accepts
          proposedChanges: {},
          status: 'pending'
        },
        mentees: [
          // Include the creator as the first mentee
          {
            id: user.id,
            email: user.email,
            name: user.name,
            role: 'mentee',
            isCreator: true
          },
          // Then add invited mentees
          ...formData.menteeEmails.filter(e => e.trim()).map((email, idx) => ({
            id: `mentee-${Date.now()}-${idx}`,
            email: email.toLowerCase(),
            name: email.split('@')[0],
            role: 'mentee',
            invited: true
          }))
        ],
        mentor: null,
        flyOnWall: null,
        omniscient: null,
        comments: [],
        steps: {
          identifyDefine: { completed: false, data: {} },
          communicate: { completed: false, cycles: [], acknowledged: false },
          exploreAlternatives: { completed: false, alternatives: [] },
          evaluateSelect: { completed: false, ratings: {}, selected: null },
          agreeImplement: { completed: false, acknowledged: [] },
          followUp: { completed: false, ratings: {} }
        }
      };

      addAuditEntry(conflict, user.id, user.name, 'Created conflict', conflict.title, user.email);
      const saved = await storage.set(`conflict:${conflict.id}`, conflict, true);
      
      if (saved) {
        
        // Harm detection on conflict description fields
        const allText = [formData.title, formData.what, formData.who, formData.where, formData.when, formData.how, formData.desiredOutcome].filter(Boolean).join(' ');
        const harmResult = detectHarmContent(allText);
        if (harmResult.detected) {
          showCrisisModal(harmResult.type);
          // No mentor to notify yet at creation time — log to safety audit
          logHarmDetectionEvent(user.id, user.name, conflict.id, harmResult.type, 'conflict creation');
        }
        
        // Send invitation emails to all mentees
        const emailPromises = formData.menteeEmails
          .filter(e => e.trim() && e.includes('@'))
          .map(async (toEmail) => {
            try {
              const result = await emailService.sendInvitation({
                toEmail,
                inviterName: user.name,
                inviterEmail: user.email,
                conflictTitle: formData.title,
                conflictId: conflict.id,
                conflictDescription: formData.what
              });
              return { email: toEmail, success: result.success, result };
            } catch (error) {
              console.error(`Failed to send email to ${toEmail}:`, error);
              return { email: toEmail, success: false, error };
            }
          });

        // Wait for all emails to be sent (but don't block on failures)
        const emailResults = await Promise.allSettled(emailPromises);
        
        // Update conflict with email sent timestamps
        const updatedMentees = conflict.mentees.map(mentee => {
          const emailResult = emailResults.find(r => 
            r.status === 'fulfilled' && r.value.email.toLowerCase() === mentee.email.toLowerCase()
          );
          if (emailResult && emailResult.value.success) {
            return {
              ...mentee,
              lastEmailSent: new Date().toISOString(),
              emailSentCount: 1
            };
          }
          return mentee;
        });
        
        // Save updated conflict with email timestamps
        const conflictWithEmailInfo = { ...conflict, mentees: updatedMentees };
        await storage.set(`conflict:${conflict.id}`, conflictWithEmailInfo, true);
        
        // Feature 3: Notify connected mentor(s) about new conflict
        // Mentor access is derived from pre-established relationships, not per-conflict assignment
        try {
          const rels = await loadRelationships(user.id);
          const mentorRels = rels.filter(r => 
            r.type === 'mentor-participant' && 
            r.status === 'active' &&
            (r.participantId === user.id || 
             (r.initiatorRole === 'participant' && r.initiatorId === user.id) ||
             (r.receiverRole === 'participant' && r.receiverId === user.id))
          );
          for (const rel of mentorRels) {
            const mentorId = rel.mentorId || (rel.initiatorRole === 'mentor' ? rel.initiatorId : rel.receiverId);
            const notifId = `notif-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
            const existingNotifs = await storage.get(`notifications:${mentorId}`, true) || [];
            const newNotif = {
              id: notifId,
              type: 'new-conflict-created',
              conflictId: conflict.id,
              conflictTitle: formData.title,
              creatorName: user.name,
              creatorEmail: user.email,
              timestamp: new Date().toISOString(),
              read: false
            };
            await storage.set(`notifications:${mentorId}`, [...(Array.isArray(existingNotifs) ? existingNotifs : []), newNotif], true);
            sendPushToUser(mentorId, 'New Conflict Created',
              `${user.name} created "${formData.title}"`,
              { conflictId: conflict.id });
          }
        } catch (e) { console.error('Mentor notification error:', e); }
        
        // Show summary if emails were configured
        if (EMAIL_CONFIG.enabled) {
          const successCount = emailResults.filter(r => r.status === 'fulfilled' && r.value.success).length;
          const totalCount = emailResults.length;
          if (successCount < totalCount) {
            toast(`Conflict created! ${successCount} of ${totalCount} invitation emails sent successfully.`);
          }
        } else {
          // Show invitation links for manual sharing
          const inviteLink = `${window.location.origin}?invite=${conflict.id}`;
        }
        
        onCreated();
      } else {
        console.error('Failed to save conflict');
        toast('Failed to save conflict. Please try again.');
        setSaving(false);
      }
    } catch (error) {
      console.error('Error creating conflict:', error);
      toast('An error occurred while creating the conflict. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen p-3 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-800 mb-6 transition-colors"
        >
          ← Back to Dashboard
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 border border-stone-200">
          <h2 className="text-2xl sm:text-3xl font-light text-stone-800 mb-6 sm:mb-8">Create New Conflict</h2>

          {/* Progress Indicator */}
          <div className="flex items-center gap-4 mb-8">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
                  s === step ? 'bg-amber-700 text-white' : s < step ? 'bg-green-500 text-white' : 'bg-stone-200 text-stone-500'
                }`}>
                  {s < step ? '✓' : s}
                </div>
                {s < 3 && <div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-green-500' : 'bg-stone-200'}`} />}
              </div>
            ))}
          </div>

          {/* Say It Your Way CTA — available on all steps */}
          {!sayItYourWayOpen && (
            <button
              onClick={() => setSayItYourWayOpen(true)}
              className="w-full p-4 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl text-white shadow-lg hover:shadow-xl transition-all hover:scale-[1.01] active:scale-[0.99] group"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center group-hover:bg-white/30 transition-colors flex-shrink-0">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <div className="text-left flex-1">
                  <div className="text-base font-bold mb-0.5">Say It Your Way</div>
                  <div className="text-xs text-purple-50">
                    Don't feel like filling out a form? Just tell us what happened, and we'll organize it for you.
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-white/90 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
              </div>
            </button>
          )}

          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="create-conflict-title" className="block text-sm font-medium text-stone-700 mb-2">
                  Conflict Title *
                </label>
                <input id="create-conflict-title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                    errors.title ? 'border-red-500' : 'border-stone-300'
                  }`}
                  placeholder="Brief title for this conflict"
                />
                {errors.title && <p className="text-red-700 text-sm mt-1">{errors.title}</p>}
              </div>

              {/* Urgency Rating */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Urgency Level
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => {
                        const updates = { urgency: level };
                        if (level >= 4) updates.responseWindow = 5;
                        else if (formData.responseWindow === 5 && formData.urgency >= 4) updates.responseWindow = 15;
                        setFormData({ ...formData, ...updates });
                      }}
                      className={`flex-1 py-3 rounded-lg font-medium text-sm transition-all border-2 ${
                        formData.urgency === level
                          ? level >= 4
                            ? 'bg-red-600 border-red-600 text-white shadow-md scale-105'
                            : level === 3
                              ? 'bg-amber-500 border-amber-500 text-stone-900 shadow-md scale-105'
                              : 'bg-blue-600 border-blue-500 text-white shadow-md scale-105'
                          : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300'
                      }`}
                    >
                      <div className="text-lg">{level}</div>
                      <div className="text-xs opacity-80 mt-0.5">
                        {level === 1 ? 'Low' : level === 2 ? 'Minor' : level === 3 ? 'Moderate' : level === 4 ? 'High' : 'Critical'}
                      </div>
                    </button>
                  ))}
                </div>
                {formData.urgency >= 4 && (
                  <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Response window set to 5 minutes for high urgency
                  </p>
                )}
              </div>

              {/* Need By Date */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Need By Date <span className="text-stone-500 font-normal">(optional)</span>
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label htmlFor="create-need-by-date" className="block text-xs text-stone-500 mb-1">Date</label>
                    <input id="create-need-by-date"
                      type="date"
                      value={formData.needByDate ? formData.needByDate.split('T')[0] : ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = formData.needByDate ? formData.needByDate.split('T')[1] || '17:00' : '17:00';
                        setFormData({ ...formData, needByDate: date ? `${date}T${time}` : '' });
                      }}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="create-need-by-time" className="block text-xs text-stone-500 mb-1">Time</label>
                    <input id="create-need-by-time"
                      type="time"
                      value={formData.needByDate ? formData.needByDate.split('T')[1] || '17:00' : ''}
                      onChange={(e) => {
                        const date = formData.needByDate ? formData.needByDate.split('T')[0] : '';
                        if (date) setFormData({ ...formData, needByDate: `${date}T${e.target.value}` });
                      }}
                      disabled={!formData.needByDate}
                      className={`w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${!formData.needByDate ? 'bg-stone-100 text-stone-600' : ''}`}
                    />
                  </div>
                </div>
                {formData.needByDate && (
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-stone-500">
                      Deadline: {new Date(formData.needByDate).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, needByDate: '' })}
                      className="text-xs text-red-700 hover:text-red-600 font-medium"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {/* Deadline Analysis */}
              {deadlineAnalysis && (
                <div className={`p-4 rounded-xl border-2 transition-all ${
                  deadlineAnalysis.fits
                    ? deadlineAnalysis.adjustments.length > 0
                      ? 'bg-amber-50 border-amber-300'
                      : 'bg-green-50 border-green-300'
                    : 'bg-red-50 border-red-300'
                } ${deadlineShake ? 'ring-2 ring-offset-2 ring-red-400' : ''}`}
                style={deadlineShake ? { animation: 'shake 0.5s ease-in-out' } : {}}
                >
                  {deadlineShake && (
                    <style>{`
                      @keyframes shake {
                        0%, 100% { transform: translateX(0); }
                        20% { transform: translateX(-8px); }
                        40% { transform: translateX(8px); }
                        60% { transform: translateX(-6px); }
                        80% { transform: translateX(6px); }
                      }
                    `}</style>
                  )}
                  {deadlineAnalysis.fits && deadlineAnalysis.adjustments.length === 0 && (
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-700 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Current settings fit your deadline</p>
                        <p className="text-xs text-green-700 mt-1">
                          Worst-case timeline: {formatMinutes(deadlineAnalysis.worstCase)} · Available: {formatMinutes(deadlineAnalysis.available)}
                        </p>
                      </div>
                    </div>
                  )}
                  {deadlineAnalysis.fits && deadlineAnalysis.adjustments.length > 0 && (
                    <div>
                      <div className="flex items-start gap-3 mb-3">
                        <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Settings adjusted to meet your deadline</p>
                          <p className="text-xs text-amber-700 mt-1">
                            Worst-case: {formatMinutes(deadlineAnalysis.worstCase)} · Available: {formatMinutes(deadlineAnalysis.available)}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-1.5 ml-8">
                        {deadlineAnalysis.adjustments.map((adj, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-amber-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                            {adj}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            responseWaitTime: deadlineAnalysis.waitTime,
                            responseWindow: deadlineAnalysis.responseWindow,
                            maxCommunicationCycles: deadlineAnalysis.maxCycles
                          });
                          toast('Settings adjusted to meet deadline');
                        }}
                        className="mt-3 ml-8 px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white text-sm rounded-lg font-medium transition-colors"
                      >
                        Apply Adjustments
                      </button>
                    </div>
                  )}
                  {!deadlineAnalysis.fits && (
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Deadline may not be achievable</p>
                        <p className="text-xs text-red-600 mt-1">{deadlineAnalysis.reason}</p>
                        <p className="text-xs text-red-700 mt-2">Consider extending the deadline or accepting that all communication cycles may not complete in time.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label htmlFor="create-response-hours" className="block text-sm font-medium text-stone-700 mb-2">
                    Response Hours
                  </label>
                  <select id="create-response-hours"
                    value={formData.responseHours === '9-5' || formData.responseHours === '5-9' || formData.responseHours === '24' ? formData.responseHours : 'custom'}
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setFormData({ ...formData, responseHours: 'custom', customStartHour: 9, customEndHour: 17 });
                      } else {
                        setFormData({ ...formData, responseHours: e.target.value });
                      }
                    }}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="9-5">9am - 5pm</option>
                    <option value="5-9">5pm - 9pm</option>
                    <option value="24">24 hours</option>
                    <option value="custom">Custom hours...</option>
                  </select>
                  
                  {/* Custom Hours Input */}
                  {(formData.responseHours === 'custom' || (!['9-5', '5-9', '24'].includes(formData.responseHours))) && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-800 font-medium mb-2">Enter custom hours</p>
                      <div className="space-y-2">
                        {/* Start Time */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-amber-700 w-12">From:</span>
                          <input
                            type="number"
                            min="1"
                            max="12"
                            value={(() => {
                              const hour = formData.customStartHour !== null ? formData.customStartHour : parseInt(formData.responseHours.split('-')[0]) || 9;
                              return hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            })()}
                            onChange={(e) => {
                              let hour12 = parseInt(e.target.value);
                              if (hour12 < 1) hour12 = 1;
                              if (hour12 > 12) hour12 = 12;
                              const currentHour = formData.customStartHour !== null ? formData.customStartHour : parseInt(formData.responseHours.split('-')[0]) || 9;
                              const isPM = currentHour >= 12;
                              const hour24 = isPM ? (hour12 === 12 ? 12 : hour12 + 12) : (hour12 === 12 ? 0 : hour12);
                              const end = formData.customEndHour !== null ? formData.customEndHour : parseInt(formData.responseHours.split('-')[1]) || 17;
                              setFormData({ ...formData, customStartHour: hour24, customEndHour: end, responseHours: `${hour24}-${end}` });
                            }}
                            className="w-14 px-2 py-1 border border-amber-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                          <select
                            value={(() => {
                              const hour = formData.customStartHour !== null ? formData.customStartHour : parseInt(formData.responseHours.split('-')[0]) || 9;
                              return hour >= 12 ? 'PM' : 'AM';
                            })()}
                            onChange={(e) => {
                              const currentHour = formData.customStartHour !== null ? formData.customStartHour : parseInt(formData.responseHours.split('-')[0]) || 9;
                              const hour12 = currentHour === 0 ? 12 : currentHour > 12 ? currentHour - 12 : currentHour;
                              const isPM = e.target.value === 'PM';
                              const hour24 = isPM ? (hour12 === 12 ? 12 : hour12 + 12) : (hour12 === 12 ? 0 : hour12);
                              const end = formData.customEndHour !== null ? formData.customEndHour : parseInt(formData.responseHours.split('-')[1]) || 17;
                              setFormData({ ...formData, customStartHour: hour24, customEndHour: end, responseHours: `${hour24}-${end}` });
                            }}
                            className="w-16 px-2 py-1 border border-amber-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                        
                        {/* End Time */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-amber-700 w-12">To:</span>
                          <input
                            type="number"
                            min="1"
                            max="12"
                            value={(() => {
                              const hour = formData.customEndHour !== null ? formData.customEndHour : parseInt(formData.responseHours.split('-')[1]) || 17;
                              return hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            })()}
                            onChange={(e) => {
                              let hour12 = parseInt(e.target.value);
                              if (hour12 < 1) hour12 = 1;
                              if (hour12 > 12) hour12 = 12;
                              const currentHour = formData.customEndHour !== null ? formData.customEndHour : parseInt(formData.responseHours.split('-')[1]) || 17;
                              const isPM = currentHour >= 12;
                              const hour24 = isPM ? (hour12 === 12 ? 12 : hour12 + 12) : (hour12 === 12 ? 0 : hour12);
                              const start = formData.customStartHour !== null ? formData.customStartHour : parseInt(formData.responseHours.split('-')[0]) || 9;
                              setFormData({ ...formData, customStartHour: start, customEndHour: hour24, responseHours: `${start}-${hour24}` });
                            }}
                            className="w-14 px-2 py-1 border border-amber-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                          <select
                            value={(() => {
                              const hour = formData.customEndHour !== null ? formData.customEndHour : parseInt(formData.responseHours.split('-')[1]) || 17;
                              return hour >= 12 ? 'PM' : 'AM';
                            })()}
                            onChange={(e) => {
                              const currentHour = formData.customEndHour !== null ? formData.customEndHour : parseInt(formData.responseHours.split('-')[1]) || 17;
                              const hour12 = currentHour === 0 ? 12 : currentHour > 12 ? currentHour - 12 : currentHour;
                              const isPM = e.target.value === 'PM';
                              const hour24 = isPM ? (hour12 === 12 ? 12 : hour12 + 12) : (hour12 === 12 ? 0 : hour12);
                              const start = formData.customStartHour !== null ? formData.customStartHour : parseInt(formData.responseHours.split('-')[0]) || 9;
                              setFormData({ ...formData, customStartHour: start, customEndHour: hour24, responseHours: `${start}-${hour24}` });
                            }}
                            className="w-16 px-2 py-1 border border-amber-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-amber-700 mt-2">
                        Example: 9 AM to 5 PM, or 5 PM to 9 PM
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="create-wait-time" className="block text-sm font-medium text-stone-700 mb-2">
                    Wait Time (min)
                  </label>
                  <select id="create-wait-time"
                    value={formData.responseWaitTime}
                    onChange={(e) => setFormData({ ...formData, responseWaitTime: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="create-response-window" className="block text-sm font-medium text-stone-700 mb-2">
                    Response Window (min)
                  </label>
                  <select id="create-response-window"
                    value={formData.responseWindow}
                    onChange={(e) => setFormData({ ...formData, responseWindow: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>
              </div>

              {/* AI Paraphrase Assistance Toggle */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-stone-700">AI Paraphrase Assistance</label>
                    <p className="text-xs text-stone-500 mt-1">
                      When enabled, participants who struggle with paraphrasing will be offered AI-generated suggestions 
                      as starting points. They must edit the suggestion before submitting. AI-assisted entries are flagged.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch" aria-label="AI paraphrase assistance" aria-checked={formData.aiParaphraseAssist}
                    onClick={() => setFormData({ ...formData, aiParaphraseAssist: !formData.aiParaphraseAssist })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                      formData.aiParaphraseAssist ? 'bg-blue-600' : 'bg-stone-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.aiParaphraseAssist ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Story Mode Toggle */}
                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-stone-700">Story Mode</label>
                      <p className="text-xs text-stone-500 mt-1">
                        When enabled, participants can view their conflict journey retold as a narrative story 
                        using their animal avatars. Helps reflect on progress in a friendly, approachable way.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch" aria-label="Story mode" aria-checked={formData.storyModeEnabled}
                      onClick={() => setFormData({ ...formData, storyModeEnabled: !formData.storyModeEnabled })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                        formData.storyModeEnabled ? 'bg-blue-600' : 'bg-stone-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.storyModeEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Problem Statement */}
          {step === 2 && (
            <div className="space-y-6">
              <p className="text-stone-600 mb-4">
                Define the conflict using the Who, What, Where, When, and How framework.
              </p>

              <div>
                <label htmlFor="create-who" className="block text-sm font-medium text-stone-700 mb-2">
                  Who is involved? *
                </label>
                <input id="create-who"
                  type="text"
                  value={formData.who}
                  onChange={(e) => setFormData({ ...formData, who: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                    errors.who ? 'border-red-500' : 'border-stone-300'
                  }`}
                  placeholder="Who are the parties involved?"
                />
                {errors.who && <p className="text-red-700 text-sm mt-1">{errors.who}</p>}
              </div>

              <div>
                <label htmlFor="create-what" className="block text-sm font-medium text-stone-700 mb-2">
                  What is the conflict? *
                </label>
                <div className="relative">
                  <textarea id="create-what"
                    value={formData.what}
                    onChange={(e) => setFormData({ ...formData, what: e.target.value })}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                      errors.what ? 'border-red-500' : 'border-stone-300'
                    }`}
                    placeholder="What is the issue?"
                    rows={3}
                  />
                  <VoiceInputButton 
                    onResult={(text) => setFormData({ ...formData, what: formData.what + (formData.what ? ' ' : '') + text })}
                    className="absolute right-2 top-2"
                  />
                </div>
                {errors.what && <p className="text-red-700 text-sm mt-1">{errors.what}</p>}
              </div>

              <div>
                <label htmlFor="create-where" className="block text-sm font-medium text-stone-700 mb-2">
                  Where did it occur? *
                </label>
                <input id="create-where"
                  type="text"
                  value={formData.where}
                  onChange={(e) => setFormData({ ...formData, where: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                    errors.where ? 'border-red-500' : 'border-stone-300'
                  }`}
                  placeholder="Where did this happen?"
                />
                {errors.where && <p className="text-red-700 text-sm mt-1">{errors.where}</p>}
              </div>

              <div>
                <label htmlFor="create-when" className="block text-sm font-medium text-stone-700 mb-2">
                  When did it occur? *
                </label>
                <input id="create-when"
                  type="text"
                  value={formData.when}
                  onChange={(e) => setFormData({ ...formData, when: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                    errors.when ? 'border-red-500' : 'border-stone-300'
                  }`}
                  placeholder="When did this happen?"
                />
                {errors.when && <p className="text-red-700 text-sm mt-1">{errors.when}</p>}
              </div>

              <div>
                <label htmlFor="create-how" className="block text-sm font-medium text-stone-700 mb-2">
                  How did it happen? *
                </label>
                <div className="relative">
                  <textarea id="create-how"
                    value={formData.how}
                    onChange={(e) => setFormData({ ...formData, how: e.target.value })}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                      errors.how ? 'border-red-500' : 'border-stone-300'
                    }`}
                    placeholder="How did this conflict arise?"
                    rows={3}
                  />
                  <VoiceInputButton 
                    onResult={(text) => setFormData({ ...formData, how: formData.how + (formData.how ? ' ' : '') + text })}
                    className="absolute right-2 top-2"
                  />
                </div>
                {errors.how && <p className="text-red-700 text-sm mt-1">{errors.how}</p>}
              </div>
            </div>
          )}

          {/* Step 3: Participants & Outcome */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="create-desired-outcome" className="block text-sm font-medium text-stone-700 mb-2">
                  Desired Outcome *
                </label>
                <div className="relative">
                  <textarea id="create-desired-outcome"
                    value={formData.desiredOutcome}
                    onChange={(e) => setFormData({ ...formData, desiredOutcome: e.target.value })}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                      errors.desiredOutcome ? 'border-red-500' : 'border-stone-300'
                    }`}
                    placeholder="What would an ideal resolution look like?"
                    rows={4}
                  />
                  <VoiceInputButton 
                    onResult={(text) => setFormData({ ...formData, desiredOutcome: formData.desiredOutcome + (formData.desiredOutcome ? ' ' : '') + text })}
                    className="absolute right-2 top-2"
                  />
                </div>
                {errors.desiredOutcome && <p className="text-red-700 text-sm mt-1">{errors.desiredOutcome}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Invite Participants * (at least 1 required)
                </label>
                {formData.menteeEmails.map((email, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="email"
                      aria-label={`Participant ${idx + 1} email`}
                      value={email}
                      onChange={(e) => {
                        const newEmails = [...formData.menteeEmails];
                        newEmails[idx] = e.target.value;
                        setFormData({ ...formData, menteeEmails: newEmails });
                      }}
                      className="flex-1 px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="participant@example.com"
                    />
                    {formData.menteeEmails.length > 1 && (
                      <button
                        onClick={() => {
                          const newEmails = formData.menteeEmails.filter((_, i) => i !== idx);
                          setFormData({ ...formData, menteeEmails: newEmails });
                        }}
                        className="p-3 text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label="Remove participant"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                {formData.menteeEmails.length < 6 && (
                  <button
                    onClick={() => setFormData({ ...formData, menteeEmails: [...formData.menteeEmails, ''] })}
                    className="text-amber-700 hover:text-amber-800 text-sm font-medium mt-2"
                  >
                    + Add another participant
                  </button>
                )}
                {errors.menteeEmails && <p className="text-red-700 text-sm mt-1">{errors.menteeEmails}</p>}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-6 py-3 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
              >
                Previous
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={saving}
              className={`px-6 py-3 bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98] ${
                step === 1 ? 'ml-auto' : ''
              }`}
            >
              {saving ? 'Creating...' : step === 3 ? 'Create Conflict' : 'Next'}
            </button>
          </div>
        </div>
      </div>

      {/* Say It Your Way Panel */}
      <SayItYourWayPanel
        isOpen={sayItYourWayOpen}
        onClose={() => setSayItYourWayOpen(false)}
        onExtracted={handleSayItYourWayExtracted}
      />

      {/* Mobile overlay when panel is open */}
      {sayItYourWayOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          onClick={() => setSayItYourWayOpen(false)}
        />
      )}
    </div>
  );
}

// Review Proposed Changes View Component

export default CreateConflictView;
